import { CatalystSubcategories } from '@api/api/catalyst-insights';
import { AssetSubClassCode } from '@api/api/entity-constants';
import { type Entity } from '@api/api/entity-schema';
import { type EventFeedEvent } from '@api/api/event-feed';
import { type GeographyCountry } from '@api/api/geographies';
import { convertCountryCodeToAlpha2 } from '@design/components/flag-icon/utils';
import { tabTypeToEventFeedType } from '@terminal/stores/use-feed-filters/useFeedFilters';
import {
  type FollowingInsightFeedFilterTabs,
  followingTabKeys,
} from '@terminal/views/insight-feed/components/insight-feed-type-tab-line/InsightFeedTypeTabLine';
import { isToday, startOfDay, subDays, subMonths } from 'date-fns';

export type OverviewEventFeedPeriod = '1D' | '3D' | '1W' | '1M';

export const getLocalDateKey = (isoDateStr: string): string => {
  return startOfDay(new Date(isoDateStr)).toISOString().split('T')[0];
};

export const isSameLocalDate = (
  dateStr1: string,
  dateStr2: string
): boolean => {
  return getLocalDateKey(dateStr1) === getLocalDateKey(dateStr2);
};

export const formatDateHeader = (
  dateStr: string,
  t: (key: string) => string
): string => {
  const date = new Date(dateStr);

  if (isToday(date)) {
    return t('whatsNew:today');
  }

  return new Intl.DateTimeFormat(navigator.language, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

export const getStartDate = (period: OverviewEventFeedPeriod): Date => {
  const start = startOfDay(new Date());

  switch (period) {
    case '1D':
      return subDays(start, 1);
    case '3D':
      return subDays(start, 3);
    case '1W':
      return subDays(start, 7);
    case '1M':
      return subMonths(start, 1);

    default: {
      return subDays(start, 7);
    }
  }
};

export const PERIOD_TITLE_KEY: Record<
  OverviewEventFeedPeriod,
  { key: string; count: number }
> = {
  '1D': { key: 'whatsNew:periodDays', count: 1 },
  '3D': { key: 'whatsNew:periodDays', count: 3 },
  '1W': { key: 'whatsNew:lastWeeks', count: 1 },
  '1M': { key: 'whatsNew:pastMonth', count: 1 },
};

export const isCountryFeedEntity = (entity?: Entity): boolean =>
  entity?.sub_class === AssetSubClassCode.EcoCountry;

export const getFeedGeography = (
  entity?: Entity,
  countries: GeographyCountry[] = []
): string | undefined => {
  if (!isCountryFeedEntity(entity)) {
    return undefined;
  }
  const code =
    convertCountryCodeToAlpha2(entity?.country?.short ?? '') ??
    convertCountryCodeToAlpha2(entity?.country?.long ?? '');
  if (code) {
    return code;
  }
  const name = entity?.country?.long.trim().toLowerCase();
  const country = countries.find(
    candidate => candidate.name.trim().toLowerCase() === name
  );
  return country
    ? (convertCountryCodeToAlpha2(country.code) ?? undefined)
    : undefined;
};

interface OverviewEventScope {
  entityTag: string;
  scenarios: boolean;
  geography?: string;
}

const COUNTRY_MACRO_SUBCATEGORIES: CatalystSubcategories[] = [
  'macroeconomic_policy_changes',
  'geopolitical_global_events',
];

const matchesOverviewScope = (
  event: EventFeedEvent,
  scope?: OverviewEventScope
): boolean => {
  if (!scope) {
    return true;
  }

  if (scope.scenarios) {
    return (
      event.type === 'scenario-insight' && event.entity === scope.entityTag
    );
  }

  if (
    event.type !== 'attribution-insight' ||
    event.metadata.category !== 'market_wide'
  ) {
    return false;
  }

  if (!scope.geography) {
    return true;
  }

  const impactedCountries = event.metadata.impacted_countries;
  return (
    Array.isArray(impactedCountries) &&
    impactedCountries.includes(scope.geography) &&
    !!event.metadata.sub_category &&
    COUNTRY_MACRO_SUBCATEGORIES.includes(event.metadata.sub_category)
  );
};

export const selectOverviewEvents = (
  events: EventFeedEvent[],
  scope?: OverviewEventScope
): EventFeedEvent[] => {
  const seen = new Set<string>();
  return events.filter(event => {
    const applicable = matchesOverviewScope(event, scope);
    const key = scope
      ? `${event.type}:${event.source_id || event.id}`
      : event.id;
    if (!applicable || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

export const getOverviewFeedScope = (
  entity: Entity | undefined,
  selectedTab: FollowingInsightFeedFilterTabs,
  countries: GeographyCountry[] = []
) => {
  const isNonStock =
    !!entity && entity.sub_class !== AssetSubClassCode.EquityStock;
  const activeTab =
    isNonStock && (selectedTab === 'all' || selectedTab === 'earnings')
      ? 'company_catalyst'
      : selectedTab;
  const tabKeys = isNonStock
    ? followingTabKeys.filter(
        key => key === 'company_catalyst' || key === 'scenarios'
      )
    : followingTabKeys;
  const geography =
    isNonStock && activeTab !== 'scenarios'
      ? getFeedGeography(entity, countries)
      : undefined;
  return {
    isNonStock,
    activeTab,
    tabKeys,
    geography,
    catalystTitleKey: isNonStock
      ? 'whatsNew:marketCatalystsTab'
      : 'stream:insightFeedTypeTabs.companyCatalysts',
  };
};

export const getOverviewFeedPayload = (
  scope: Pick<
    ReturnType<typeof getOverviewFeedScope>,
    'isNonStock' | 'activeTab' | 'geography'
  >,
  entityTag: string,
  period: OverviewEventFeedPeriod
) => ({
  entities: scope.geography ? undefined : [entityTag],
  geographies: scope.geography ? [scope.geography] : undefined,
  categories:
    scope.isNonStock && scope.activeTab !== 'scenarios'
      ? ['market_catalyst']
      : undefined,
  type: tabTypeToEventFeedType[scope.activeTab],
  date_from: getStartDate(period).toISOString(),
  limit: 100,
});
